import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

/** Where the browser says this computer or phone is. */
export interface DeviceLocation {
  longitude: number;
  latitude: number;
  /** Radius in metres within which the browser is fairly sure the position lies. */
  accuracyM: number;
}

const LOCATE_TIMEOUT_MS = 10_000;
/** A fix this young is reused, so pressing the button twice in a row answers at once. */
const LOCATE_MAX_AGE_MS = 30_000;

/**
 * Asks the browser for the position of the device the map is open on.
 *
 * Once per press rather than watching continuously (`clear` hides the dot again): the map is a fleet map, and following the
 * reader's own phone around would fight with following a vehicle. Inside MyGeotab the add-in runs
 * in a frame, and whether the browser lets a frame ask for the position is up to MyGeotab — when
 * it refuses, the reader gets told so instead of the button silently doing nothing.
 */
export function useDeviceLocation(onLocated: (location: DeviceLocation) => void) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [location, setLocation] = useState<DeviceLocation | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const locate = useCallback(() => {
    if (!("geolocation" in navigator)) {
      enqueueSnackbar(t("map.myLocation.unsupported"), { variant: "warning" });
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const next: DeviceLocation = {
          longitude: coords.longitude,
          latitude: coords.latitude,
          accuracyM: coords.accuracy,
        };
        setIsLocating(false);
        setLocation(next);
        onLocated(next);
      },
      error => {
        setIsLocating(false);
        const message =
          error.code === error.PERMISSION_DENIED
            ? t("map.myLocation.denied")
            : error.code === error.TIMEOUT
              ? t("map.myLocation.timeout")
              : t("map.myLocation.unavailable");
        enqueueSnackbar(message, { variant: "warning" });
      },
      { enableHighAccuracy: true, timeout: LOCATE_TIMEOUT_MS, maximumAge: LOCATE_MAX_AGE_MS },
    );
  }, [enqueueSnackbar, onLocated, t]);

  const clear = useCallback(() => setLocation(null), []);

  return { location, isLocating, locate, clear };
}
